import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

export type WatchProgressChipSize = 'xs' | 'sm';
export type WatchProgressChipState = 'not-started' | 'watching' | 'watched';

/**
 * WatchProgressChip — the ONE way this app states how much of a video a learner
 * has watched (KAN-306).
 *
 * Before this component there were three competing labels for the same number:
 * "(Progress: 97%)" in the player control bar, "Video Complete - 97%" /
 * "Video Progress: 97%" in the status bar, and "Progress: 97% watched" in the
 * quiz panel. Three renderers of one metric is what let the player and the badge
 * show different numbers at the same instant (Devina's KAN-306 AC#3/#7 failures:
 * player "97% watched" vs badge "Video Complete - 94%"). Reusing ONE primitive
 * fed by ONE value makes that divergence structurally impossible rather than
 * merely fixed — there is no second place left for a number to be computed.
 *
 * The chip is deliberately DUMB. `percent` is the max-watched high-water mark
 * (monotonic, backend-enforced via GREATEST()); this component never clamps,
 * recomputes, or floors it — it only rounds for display. Resume position is a
 * separate field and must never be piped in here (KAN-306 invariant (c)).
 *
 * The completed state KEEPS the live number and keeps climbing past the
 * completion threshold (KAN-307 fold-in). A terminal state that collapses to a
 * bare "Complete" is what disqualified three variants of the 2026-07-13 design
 * run — dropping the number reintroduces the ambiguity this ticket exists to
 * kill.
 *
 * Design: .design/runs/kan306-progress-labelling-20260713-2138 (winner
 * "stateful-chip", 44/50).
 */
@Component({
  selector: 'app-watch-progress-chip',
  standalone: true,
  imports: [CommonModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './watch-progress-chip.component.html',
  styleUrls: ['./watch-progress-chip.component.scss'],
})
export class WatchProgressChipComponent {
  /**
   * The one metric: max-watched completion % (monotonic high-water mark).
   * NOT the playhead position — that drives the scrubber only and is never a
   * "watched" claim.
   */
  @Input() percent = 0;

  /** videoCompleted / videoCompletedFromBackend. */
  @Input() completed = false;

  /** xs in the player control bar, sm in the status bar / quiz panel. */
  @Input() size: WatchProgressChipSize = 'sm';

  /** Variant for the video control strip, which has its own surface tints. */
  @Input() onVideo = false;

  /** Pulse the dot while playback ticks are arriving. */
  @Input() live = false;

  get state(): WatchProgressChipState {
    if (this.completed) return 'watched';
    return this.percent > 0 ? 'watching' : 'not-started';
  }

  /**
   * Fill geometry only. A CSS width outside 0-100% would paint outside the
   * chip, so the GAUGE is clamped — the displayed NUMBER never is. If an
   * out-of-range percent ever arrives it stays visible in the label as a data
   * bug to chase, rather than being silently hidden here.
   */
  get fillWidth(): number {
    return Math.min(100, Math.max(0, this.percent || 0));
  }

  get label(): string {
    return this.state === 'not-started' ? 'Not started' : 'Watched';
  }

  /**
   * The visible label uses a "·" separator that screen readers announce
   * inconsistently, so assistive tech gets the same facts as a clean phrase.
   * Not a live region: at one tick per 500ms an aria-live chip would talk over
   * everything else on the page.
   */
  get ariaLabel(): string {
    return this.state === 'not-started' ? 'Not started' : `Watched ${Math.round(this.percent)}%`;
  }

  get iconSize(): number {
    // IconComponent's size map has no 'xs' key — it silently falls back to
    // 24px, which would dwarf the chip. Pass the pixel size explicitly.
    return this.size === 'xs' ? 12 : 13;
  }
}
